import { useEffect, useState } from 'react';
import { Download, RotateCcw, Trash2 } from 'lucide-react';

// lucide-react dropped brand icons, so the Instagram glyph is inlined in
// the same 24px/2px-stroke style the lucide icons use.
function InstagramIcon({ size = 14 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}
import { api, ApiError } from '../api/client.js';
import ConfirmModal from './ConfirmModal.jsx';

// The packages whose Top 8 is the VALORANT pair of screens (valorant/
// top_8.html and champion.html) rather than Tekken 8's: each entry carries
// her agent (functions/_lib/valorantTop8.js), shown here as the game's
// headshot with a dropdown to pick another, and the exports are the Top 8
// and the champion alone (no Instagram portrait). The roster for the
// dropdown is the mirrored art's manifest.
const AGENT_PACKAGES = new Set(['valorant']);
const AGENTS_MANIFEST = '/overlay/images/valorant/characters/agents.json';
const agentIcon = (slug) => `/overlay/images/valorant/characters/icons/${slug || 'fallback'}.png`;

// Top 8 snapshot card on the PackageProduction page.
//
// Workflow: pick an event from the dropdown → the Top 8 is generated
// automatically (no button). A blurred loader covers the card while a
// generation runs so the dropdown can't be re-triggered and start.gg isn't
// hammered. When it finishes the loader clears, the stored snapshot renders,
// and the dropdown resets so re-picking the same event refreshes it.
//
// For AGENT_PACKAGES a pick in an entry's agent dropdown goes to the parent
// (onAgentPick(index, agent | null)), which writes it into state.top8 — the
// console's autosave carries it, and a re-snapshot keeps it for the same
// entrant. (Neither page shows sponsors since 2026-10-30; display.top8.sponsors
// stays in the state for older saves and means nothing.)
//
// The OBS overlay just loads the latest stored snapshot on refresh — nothing
// here talks to the overlay directly. State changes go through the parent's
// onRefresh (refetch the whole package config + silence the next autosave),
// matching the handleUpload / handleDelete pattern used for sponsor logos.
// The event list (events/eventsLoading/eventsError) is owned by
// PackageProduction: the same /api/tournament/events response that powers
// the Connection tab's "Connected" check. The card fetching its own copy
// meant a duplicate start.gg request on load and another on every
// Overlays-tab revisit (the pane is conditionally rendered, so the card
// remounts). onRefreshEvents re-fetches on demand — the way to pick up
// events added on start.gg mid-tournament.
export default function Top8SnapshotCard({
  packageSlug,
  tournamentSlug,
  top8,
  events,
  eventsLoading,
  eventsError,
  onRefreshEvents,
  onRefresh,
  onFlash,
  showLogo,
  onToggleLogo,
  onAgentPick,
}) {
  const [selectedEventId, setSelectedEventId] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('Working…');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const withAgents = AGENT_PACKAGES.has(packageSlug);
  const [agents, setAgents] = useState([]); // [{ slug, name }], the dropdown's roster

  // A slug change invalidates the current selection (the list it pointed
  // into belongs to the old tournament).
  useEffect(() => { setSelectedEventId(''); }, [tournamentSlug]);

  useEffect(() => {
    if (!withAgents) return undefined;
    let gone = false;
    fetch(AGENTS_MANIFEST)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => { if (!gone && m?.agents) setAgents(m.agents.map((a) => ({ slug: a.slug, name: a.name }))); })
      .catch(() => {});
    return () => { gone = true; };
  }, [withAgents]);

  async function runSnapshot(eventIdToUse) {
    if (!eventIdToUse) return;
    setBusyLabel('Generating Top 8…');
    setBusy(true);
    try {
      await api.packageSnapshotTop8(packageSlug, eventIdToUse);
      await onRefresh();
      onFlash?.('success', 'Top 8 generated');
    } catch (e) {
      onFlash?.('danger', e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
      // Reset to the placeholder so picking the same event again re-generates
      // it (a <select> won't fire onChange for an unchanged value).
      setSelectedEventId('');
    }
  }

  async function runClear() {
    setBusyLabel('Clearing snapshot…');
    setBusy(true);
    try {
      await api.packageClearTop8(packageSlug);
      await onRefresh();
      onFlash?.('success', 'Top 8 snapshot cleared');
    } catch (e) {
      onFlash?.('danger', e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Server-side render the standings overlay to a PNG (via Cloudflare Browser
  // Rendering) and save it — an easy way to post the Top 8 to social.
  // Formats: 'landscape' (the Top 8), 'portrait' (Tekken 8's Instagram card)
  // and 'champion' (the VALORANT champion screen).
  const FORMATS = {
    landscape: { qs: '', file: 'top-8.png', busy: 'Rendering PNG…', done: 'Top 8 PNG downloaded' },
    portrait: { qs: '?format=portrait', file: 'top-8-portrait.png', busy: 'Rendering Instagram card…', done: 'Top 8 portrait downloaded' },
    champion: { qs: '?format=champion', file: 'champion.png', busy: 'Rendering champion…', done: 'Champion PNG downloaded' },
    championPortrait: { qs: '?format=champion-portrait', file: 'champion-portrait.png', busy: 'Rendering champion portrait…', done: 'Champion portrait downloaded' },
  };
  async function runDownload(format) {
    const f = FORMATS[format] ?? FORMATS.landscape;
    setBusyLabel(f.busy);
    setBusy(true);
    try {
      const resp = await fetch(
        `/api/packages/${encodeURIComponent(packageSlug)}/top8-image${f.qs}`,
        { credentials: 'same-origin' },
      );
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Render failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.file;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onFlash?.('success', f.done);
    } catch (e) {
      onFlash?.('danger', e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Selecting an event immediately generates the Top 8 for it.
  function handleSelect(e) {
    const id = e.target.value;
    setSelectedEventId(id);
    if (id) runSnapshot(id);
  }

  function relativeTime(iso) {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    const mins = Math.floor(ms / 60_000);
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} h ago`;
    return `${Math.floor(hours / 24)} d ago`;
  }

  // 1 → "1st", 2 → "2nd", 5 → "5th", 7 → "7th" (Top 8 placements include ties).
  function ordinal(n) {
    const num = Number(n);
    if (!Number.isFinite(num)) return String(n ?? '');
    const rem100 = num % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${num}th`;
    const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[num % 10] || 'th';
    return `${num}${suffix}`;
  }

  // Pick the smallest available variant for the admin preview thumb.
  // stockIcon is the head-shot in start.gg's Tekken 8 set; fall through to
  // any image if stockIcon is missing for a given character.
  //
  // Unsealed entries (Grand Finals not yet reported in start.gg → top
  // placements ambiguous) return null so the dashed empty box renders
  // instead of an arbitrary character icon. Matches the OBS overlay's
  // fallback behaviour in paintCharImage().
  function previewThumbUrl(entry) {
    if (entry?.unsealed) return null;
    if (withAgents) return agentIcon(entry?.agent?.slug);
    const imgs = entry?.character?.images ?? [];
    return imgs.find((i) => i.type === 'stockIcon')?.url ?? imgs[0]?.url ?? null;
  }

  // The agent dropdown's first option says where the shown agent came from
  // when it wasn't picked here, so a wrong automatic one is easy to spot.
  function autoLabel(entry) {
    const auto = 'autoAgent' in (entry ?? {}) ? entry.autoAgent : entry?.agent;
    if (!auto) return 'No agent found';
    const how = auto.source === 'startgg' ? 'start.gg' : 'her main';
    return `${auto.name} (${how})`;
  }

  const CARD_HEADER = (
    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
      <h6 className="mb-0">
        <img src="/images/section-icon.png" className="pp-icon" alt="" aria-hidden="true" /> Top 8
      </h6>
      {top8 && (
        <small className="text-muted">
          {top8.eventName ?? '—'} · generated {relativeTime(top8.fetchedAt)}
        </small>
      )}
    </div>
  );

  // Blurred overlay + labelled spinner while a snapshot is generating (or
  // clearing). Rendered as a direct child of the .card.card-fui so the
  // .card-loading-overlay positioning rule applies. It also blocks pointer
  // events, which is what stops repeat triggers during a generation.
  const loadingOverlay = busy ? (
    <div className="card-loading-overlay" aria-live="polite" aria-busy="true">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <span
          className="loader"
          style={{ width: 34, height: 34, borderWidth: 3 }}
          role="status"
          aria-label={busyLabel}
        />
        <span className="small" style={{ color: 'var(--text-secondary)' }}>{busyLabel}</span>
      </div>
    </div>
  ) : null;

  if (!tournamentSlug) {
    return (
      <div className="card h-100 card-fui">
        {CARD_HEADER}
        <div className="card-body">
          <p className="text-muted small mb-0">
            Enter a start.gg tournament slug in the box above to enable Top 8 generation.
          </p>
        </div>
      </div>
    );
  }

  const entries = top8 && Array.isArray(top8.entries) ? top8.entries : [];

  return (
    <div className="card h-100 card-fui">
      {CARD_HEADER}
      <div className="card-body">
        {eventsError && (
          <div className="alert alert-warning small py-2 mb-2">{eventsError}</div>
        )}

        <div className="d-flex justify-content-between align-items-center">
          <label className="form-label small" htmlFor="top8-event">
            Event from <code>{tournamentSlug}</code>
          </label>
          <button
            type="button"
            className="btn-icon"
            disabled={eventsLoading || busy}
            onClick={onRefreshEvents}
            title="Refresh event list from start.gg"
            aria-label="Refresh event list"
          >
            <RotateCcw size={14} aria-hidden="true" />
          </button>
        </div>
        <select
          id="top8-event"
          className="form-select form-select-sm"
          value={selectedEventId}
          onChange={handleSelect}
          disabled={eventsLoading || busy}
        >
          <option value="">
            {eventsLoading
              ? 'Loading events…'
              : (events?.length ? 'Select an event to generate…' : 'No events found')}
          </option>
          {(events ?? []).map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.name}
              {/* start.gg returns the enum name string for state, not the int. */}
              {ev.state === 'COMPLETED' ? '  (completed)' : ''}
            </option>
          ))}
        </select>
        <p className="text-muted small mt-2 mb-0">
          {withAgents
            ? 'Picking an event generates the Top 8 automatically. Each player gets the agent she mains, found through the Riot IDs in the Up Next card; pick another under her name if it is wrong.'
            : 'Picking an event generates the Top 8 automatically. Refresh the overlay in OBS to see the update.'}
        </p>

        <div className="form-check mt-3">
          <input
            id="top8-show-logo"
            type="checkbox"
            className="form-check-input"
            checked={showLogo}
            onChange={(e) => onToggleLogo?.(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="top8-show-logo">
            Show Tournament Logo
          </label>
        </div>

        {top8 ? (
          <>
            {entries.length > 0 ? (
              <div
                className="mt-3"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}
              >
                {entries.map((e, index) => {
                  const thumb = previewThumbUrl(e);
                  return (
                    <div
                      key={e.entrantId ?? `${e.placement}-${e.gamerTag}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 5,
                        padding: '8px 4px',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        background: 'var(--bg-card-alt)',
                        minWidth: 0,
                        textAlign: 'center',
                      }}
                    >
                      <span style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-blue)', fontSize: 13, lineHeight: 1 }}>
                        {ordinal(e.placement)}
                      </span>
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 3 }}
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          style={{
                            width: 48, height: 48, borderRadius: 3,
                            background: 'var(--bg-card)', border: '1px dashed var(--border)',
                          }}
                        />
                      )}
                      <span
                        style={{
                          maxWidth: '100%', fontSize: 12, fontWeight: 600,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {e.unsealed ? '' : (e.displayName ?? e.gamerTag ?? '?')}
                      </span>
                      {withAgents && !e.unsealed && (
                        <select
                          className="form-select form-select-sm"
                          style={{ fontSize: 11, padding: '2px 20px 2px 6px' }}
                          value={e.agent?.source === 'pick' ? (e.agent.slug ?? '') : ''}
                          disabled={busy}
                          aria-label={`Agent for ${e.displayName ?? e.gamerTag ?? 'this player'}`}
                          onChange={(ev) => {
                            const slug = ev.target.value;
                            const found = agents.find((a) => a.slug === slug);
                            onAgentPick?.(index, found ? { slug: found.slug, name: found.name } : null);
                          }}
                        >
                          <option value="">{autoLabel(e)}</option>
                          {agents.map((a) => (
                            <option key={a.slug} value={a.slug}>{a.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted small mt-3 mb-0">
                This snapshot has no entries. (Event probably had no standings yet.)
              </p>
            )}
            <div className="d-flex gap-2 mt-3 flex-wrap">
              <button
                type="button"
                className="btn-ghost btn-ghost--primary"
                disabled={busy}
                onClick={() => runDownload('landscape')}
                title="Render the Top 8 overlay to a 16:9 PNG you can post"
              >
                <Download size={14} aria-hidden="true" />
                {withAgents ? 'Download Top 8' : 'Download PNG'}
              </button>
              {withAgents ? (
                <>
                <button
                  type="button"
                  className="btn-ghost btn-ghost--primary"
                  disabled={busy}
                  onClick={() => runDownload('champion')}
                  title="Render the champion alone with her agent to a 16:9 PNG"
                >
                  <Download size={14} aria-hidden="true" />
                  Download Champion
                </button>
                <button
                  type="button"
                  className="btn-ghost btn-ghost--primary"
                  disabled={busy}
                  onClick={() => runDownload('championPortrait')}
                  title="The champion card framed to its middle, 4:5, for a post"
                >
                  <Download size={14} aria-hidden="true" />
                  Champion 4:5
                </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn-ghost btn-ghost--primary"
                  disabled={busy}
                  onClick={() => runDownload('portrait')}
                  title="Render a 3:4 vertical card for Instagram"
                >
                  <InstagramIcon size={14} />
                  Instagram
                </button>
              )}
              <button
                type="button"
                className="btn-ghost btn-ghost--danger"
                disabled={busy}
                onClick={() => setConfirmClearOpen(true)}
              >
                <Trash2 size={14} aria-hidden="true" />
                Clear
              </button>
            </div>
          </>
        ) : (
          <p className="text-muted small mt-3 mb-0">
            No Top 8 generated yet — pick an event above to create one.
          </p>
        )}
      </div>
      <ConfirmModal
        open={confirmClearOpen}
        title="Clear Top 8 snapshot?"
        body="This removes the stored snapshot for this package. The OBS overlay will fall back to its empty state until you generate again."
        confirmLabel="Clear"
        confirmKind="danger"
        onConfirm={runClear}
        onClose={() => setConfirmClearOpen(false)}
      />
      {loadingOverlay}
    </div>
  );
}
